import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, CalendarCheck } from "lucide-react";
import { TEST_LIST, TEST_CATEGORIES, waLink } from "../data/siteConfig";
import FadeIn from "./FadeIn";

const POPULAR = ["CBC", "Thyroid", "Diabetes", "Vitamin D", "Liver Function", "Kidney Function", "Lipid Profile"];

const TestFinder = ({ preview = false }) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEST_LIST.filter((t) => {
      const matchQ = !q || t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q);
      const matchC = !category || t.category === category;
      return matchQ && matchC;
    });
  }, [query, category]);

  const shown = preview && !query && !category ? results.slice(0, 6) : results;

  return (
    <div>
      <FadeIn>
        <div className="relative">
          <Search size={20} className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search CBC, Thyroid, Vitamin D, Diabetes, Liver Function..."
            aria-label="Search for a test"
            data-testid="test-search-input"
            className="min-h-[56px] w-full rounded-full border border-gray-200 bg-white py-4 pl-14 pr-5 text-base shadow-sm focus:border-[#1E88E5] focus:outline-none focus:ring-2 focus:ring-[#1E88E5]/30"
          />
        </div>
      </FadeIn>

      <FadeIn delay={0.05} className="mt-5">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Popular Searches</p>
        <div className="flex flex-wrap gap-2">
          {POPULAR.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setQuery(p)}
              data-testid={`popular-search-${p.toLowerCase().replace(/\s+/g, "-")}`}
              className="min-h-[40px] rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-[#1E88E5] hover:text-[#1E88E5]"
            >
              {p}
            </button>
          ))}
        </div>
      </FadeIn>

      {!preview && (
        <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filter by category">
          <button
            type="button"
            onClick={() => setCategory("")}
            data-testid="category-filter-all"
            className={`min-h-[40px] rounded-full px-4 py-2 text-sm font-medium transition-colors ${!category ? "bg-[#0B3056] text-white" : "border border-gray-200 bg-white text-slate-600 hover:border-[#1E88E5]"}`}
          >
            All
          </button>
          {TEST_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(category === c ? "" : c)}
              data-testid={`category-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`min-h-[40px] rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === c ? "bg-[#0B3056] text-white" : "border border-gray-200 bg-white text-slate-600 hover:border-[#1E88E5]"}`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((t) => (
          <FadeIn key={t.name} className="h-full">
            <article className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md" data-testid={`test-card-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1E88E5]">{t.category}</span>
              <h3 className="mt-2 font-heading text-lg font-bold text-[#0B3056]">{t.name}</h3>
              <p className="mt-1.5 flex-1 text-sm text-slate-600">{t.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <a
                  href={waLink(`Hello, I would like to enquire about the ${t.name} test at Dr Lal PathLabs Collection Center, Bhopal.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`test-enquire-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-full border border-[#25D366] px-3 py-2 text-xs font-semibold text-[#1eb856] transition-colors hover:bg-[#25D366]/10"
                >
                  <MessageCircle size={15} aria-hidden="true" /> Enquire
                </a>
                <Link
                  to={`/book-a-test?test=${encodeURIComponent(t.name)}`}
                  data-testid={`test-book-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-full bg-[#1E88E5] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#1565C0]"
                >
                  <CalendarCheck size={15} aria-hidden="true" /> Book Test
                </Link>
              </div>
            </article>
          </FadeIn>
        ))}
      </div>

      {shown.length === 0 && (
        <div className="mt-8 rounded-2xl bg-[#F0F7FA] p-8 text-center" data-testid="test-search-no-results">
          <p className="text-sm text-slate-600">
            No matching test found in this list. Please contact our collection center — many more investigations are
            available on enquiry.
          </p>
          <a
            href={waLink(`Hello, I would like to enquire about the "${query}" test at Dr Lal PathLabs Collection Center, Bhopal.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="test-search-enquire-whatsapp"
            className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1eb856]"
          >
            <MessageCircle size={17} aria-hidden="true" /> Enquire on WhatsApp
          </a>
        </div>
      )}

      {preview && (
        <div className="mt-8 text-center">
          <Link
            to="/tests"
            data-testid="view-all-tests-link"
            className="inline-flex min-h-[44px] items-center rounded-full border-2 border-[#1E88E5] px-6 py-3 text-sm font-semibold text-[#1E88E5] transition-colors hover:bg-[#F0F7FA]"
          >
            View All Tests
          </Link>
        </div>
      )}
    </div>
  );
};

export default TestFinder;
